import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { getLogoConfig, LogoLocation, getTextColor } from '../../utils/logoConfig';

interface LogoProps {
  location: LogoLocation;
  className?: string;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  showSecondary?: boolean; // Pour forcer l'affichage du texte secondaire (splash screen)
}

/**
 * Composant Logo réutilisable qui s'adapte automatiquement selon :
 * - La langue (fr/en/ar)
 * - Le thème (dark/light)
 * - La taille d'écran (mobile/desktop)
 * - L'emplacement (navbar/footer/splash)
 */
export const Logo: React.FC<LogoProps> = ({ 
  location, 
  className = '',
  size = 'medium',
  showSecondary = false 
}) => {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const { isMobile } = useDeviceDetection();
  
  // Déterminer la langue
  const language = i18n.language.startsWith('ar') 
    ? 'ar' 
    : i18n.language.startsWith('en') 
    ? 'en' 
    : 'fr';
  
  // Obtenir la configuration du logo
  const config = getLogoConfig(
    language,
    theme,
    isMobile ? 'mobile' : 'desktop',
    location
  );
  
  // Tailles de texte selon la prop size
  // Ajustement spécial pour le mini-logo de la navbar mobile (doit être plus grand)
  const isNavbarMobileMini = location === 'navbar' && isMobile && config.isMini;
  
  const sizeClasses = {
    small: 'text-xl',
    medium: 'text-2xl',
    large: 'text-4xl',
    xlarge: 'text-6xl',
  };
  
  const secondarySizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-xl',
    xlarge: 'text-2xl',
  };
  
  // Ajustements spéciaux selon l'emplacement
  let baseSizeClass = sizeClasses[size];
  const isLogin = location === 'login';
  
  // Mini-logo navbar mobile = text-4xl (peu importe la prop size)
  if (isNavbarMobileMini) {
    baseSizeClass = 'text-4xl';
  }
  // Pour le splash screen, utiliser une taille encore plus grande pour le mini-logo principal
  else if (config.isSplash) {
    baseSizeClass = 'text-8xl';
  }
  // Pour le logo sur la page de login, utiliser aussi text-8xl
  else if (isLogin) {
    baseSizeClass = 'text-8xl';
  }
  
  const secondarySizeClass = secondarySizeClasses[size];
  
  // Pour le splash screen, afficher deux logos
  if (config.isSplash || showSecondary) {
    // Pour le logo secondaire, extraire la lettre principale et le reste
    let secondaryPrimaryLetter = '';
    let secondaryRestText = '';
    
    if (config.secondaryText) {
      if (config.isRTL) {
        // Pour l'arabe : "ء خدمات" -> "ء" + " خدمات"
        const parts = config.secondaryText.split(' ');
        secondaryPrimaryLetter = parts[0]; // "ء"
        secondaryRestText = ' ' + parts.slice(1).join(' '); // " خدمات"
      } else {
        // Pour fr/en : "eServices" -> "e" + "Services"
        secondaryPrimaryLetter = config.secondaryText[0]; // "e"
        secondaryRestText = config.secondaryText.slice(1); // "Services"
      }
    }
    
    return (
      <div className={`flex flex-col items-center ${className}`} dir={config.isRTL ? 'rtl' : 'ltr'}>
        {/* Grand mini-logo principal */}
        <div className={`${baseSizeClass} font-extrabold tracking-tight mb-2`}>
          <span className={getTextColor(theme, true)}>{config.primaryLetter}</span>
          <span className={getTextColor(theme, false)}>{config.restText}</span>
        </div>
        
        {/* Logo textuel secondaire */}
        {config.secondaryText && (
          <div className={`${secondarySizeClass} font-extrabold tracking-tight`}>
            <span className={getTextColor(theme, true)}>{secondaryPrimaryLetter}</span>
            <span className={getTextColor(theme, false)}>{secondaryRestText}</span>
          </div>
        )}
      </div>
    );
  }
  
  // Pour les autres emplacements (navbar, footer)
  return (
    <div className={`${baseSizeClass} font-bold tracking-tight ${className}`} dir={config.isRTL ? 'rtl' : 'ltr'}>
      <span className={getTextColor(theme, true)}>{config.primaryLetter}</span>
      <span className={getTextColor(theme, false)}>{config.restText}</span>
    </div>
  );
};

export default Logo;
